import { type FC, useMemo } from 'react';
import { vegaCopyConversation } from '@gonfalon/dogfood-flags/dogfood-flags';
import { DateFormat } from '@gonfalon/format/constants';
import { format } from '@gonfalon/format/format';
import { msg } from '@gonfalon/messages/msg';
import { Box } from '@gonfalon/observability-ui/components/Box';

import { ContextPills } from '../../../../../components/AgentInsights/components/messages/ContextPills';
import { InlineContextMessage } from '../../../../../components/AgentInsights/components/messages/InlineContextMessage';
import { ConversationMessageCard } from '../../../../../components/conversation/ConversationMessageCard';
import { parseMessageMentions } from '../../../../../components/mentions';
import type { ResourceOrQuery } from '../../../../../resources';
import { trackVegaAiMessageCopied } from '../../../../../vegaAnalytics';
import { CopyButton } from '../CopyButton';

import { copyHoverGroup } from '../../../conversation/ConversationChrome.css';

// Component for rendering user messages
export const UserMessage: FC<{
  message: string;
  context?: ResourceOrQuery[];
  timestamp?: string;
  redesign?: boolean;
}> = ({ message, context, timestamp, redesign = false }) => {
  const copyEnabled = vegaCopyConversation();

  const formattedTimestamp = timestamp ? format(timestamp, DateFormat.MMM_D_H_MM_A) : null;
  const meta = !redesign && formattedTimestamp ? [{ key: 'time', value: formattedTimestamp }] : undefined;
  const time = redesign && formattedTimestamp ? formattedTimestamp : undefined;
  const hasCaption = Boolean(meta) || Boolean(time) || copyEnabled;

  const externalContext = useMemo(() => {
    if (!context?.length) {
      return undefined;
    }
    const inlineMentionIds = new Set(
      parseMessageMentions(message).flatMap((s) => (s.type === 'mention' && s.mention ? [s.mention.id] : [])),
    );
    return context.filter((r) => !inlineMentionIds.has(r.id));
  }, [message, context]);

  return (
    <ConversationMessageCard
      speaker="user"
      variant="chat"
      redesign={redesign}
      meta={meta}
      time={time}
      className={hasCaption ? copyHoverGroup : undefined}
      hoverRevealCaption={hasCaption}
      copyText={redesign && copyEnabled ? message : undefined}
      onCopy={redesign ? () => trackVegaAiMessageCopied('user') : undefined}
      footer={
        !redesign && copyEnabled ? (
          <CopyButton
            text={message}
            label={msg('vega.conversation.copyUserMessageLabel')}
            successTitle={msg('vega.conversation.copyUserMessageSuccess')}
            onCopy={() => trackVegaAiMessageCopied('user')}
          />
        ) : undefined
      }
    >
      {externalContext && externalContext.length > 0 && (
        <Box marginBottom="8">
          <ContextPills context={externalContext} />
        </Box>
      )}

      <Box style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}>
        <InlineContextMessage message={message} />
      </Box>
    </ConversationMessageCard>
  );
};
